import { Link } from "react-router-dom";
import type { TouristSpot } from "@/services/touristSpotService";
import SafeImage from "@/components/base/SafeImage";
import SocialShare from "@/components/feature/SocialShare";
import ShareCounter from "@/components/feature/ShareCounter";
import { trackShare } from "@/services/shareService";
import { getSpotFallbackImage } from "@/utils/fallbackImage";

interface SpotHeroProps {
  spot: TouristSpot;
}

export default function SpotHero({ spot }: SpotHeroProps) {
  const shortAddress = spot.address.split(",").slice(-3).join(",").trim();

  return (
    <section className="w-full">
      <div className="relative h-52 w-full overflow-hidden bg-foreground-950 md:h-72">
        <SafeImage
          src={spot.image}
          fallback={getSpotFallbackImage(spot)}
          alt={`${spot.name} - ${spot.category} in ${spot.districtName}`}
          title={`${spot.name} - ${spot.category} in ${spot.districtName}`}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/55 via-foreground-950/10 to-transparent" />
      </div>

      <div className="mx-auto w-full max-w-[1400px] px-6">
        <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6 md:mt-8 md:p-8">
          <nav
            className="flex flex-wrap items-center gap-2 text-xs text-foreground-500"
            aria-label="Breadcrumb"
          >
            <Link to="/" className="transition-colors hover:text-primary-700">
              Home
            </Link>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <Link
              to={`/location/${spot.districtSlug}`}
              className="transition-colors hover:text-primary-700"
            >
              {spot.districtName}
            </Link>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <span className="font-medium text-foreground-700">{spot.name}</span>
          </nav>

          <div className="mt-5 flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-accent-900">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-camera-lens-line text-[13px]" />
                </span>
                {spot.category}
              </span>

              <h1 className="mt-3 font-heading text-2xl font-extrabold leading-tight text-foreground-950 md:text-3xl">
                {spot.name}
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                {spot.blurb}
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
                <span className="flex items-center gap-2">
                  <span className="flex h-4 w-4 items-center justify-center text-secondary-600">
                    <i className="ri-time-line text-base" />
                  </span>
                  {spot.timings}
                </span>
                <span className="flex items-center gap-2">
                  <span className="flex h-4 w-4 items-center justify-center text-secondary-600">
                    <i className="ri-sun-line text-base" />
                  </span>
                  Best time: {spot.bestTime}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-4 items-start lg:items-end">
              <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-stretch sm:gap-6 lg:justify-end">
                <div className="w-full shrink-0 rounded-md border border-background-200 bg-background-100 px-5 py-4 sm:w-52">
                  <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500">
                    Entry fee
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-foreground-950">{spot.entryFee}</p>
                  <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-foreground-500">
                    Ideal duration
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-foreground-950">
                    {spot.idealDuration}
                  </p>
                </div>
                <div className="w-full rounded-md border border-background-200 bg-background-100 px-5 py-4 sm:w-auto">
                  <div className="flex flex-col items-start gap-2">
                    <SocialShare
                      title={spot.name}
                      label="Share this place"
                      onShare={(platform) => trackShare("spot", spot.slug, spot.name, platform)}
                    />
                    <ShareCounter entityType="spot" slug={spot.slug} alwaysVisible />
                  </div>
                </div>
              </div>

              <span className="flex items-center gap-2 text-sm text-foreground-600">
                <span className="flex h-4 w-4 items-center justify-center text-red-500">
                  <i className="ri-map-pin-line text-base" />
                </span>
                {shortAddress}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}